/**
 * This component represents a dialog for adding a price configuration.
 * It allows the user to select an Independent System Operator, choose a power purchase agreement type,
 * select a prize zone, and review the form before submission.
 *
 * @component
 * @example
 * return (
 *   <AddPriceConfigDialog />
 * )
 */
import React, { useEffect } from "react";
import {
  StyledIconButton,
  PrimaryButton,
  StyledRadio,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog.js";
import {
  BodyMedium,
  H6HeadlineSmall,
  H8TitleMedium,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControlLabel,
  RadioGroup,
  Stack,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";

import {
  StyledCheckbox,
  StyledFormControl,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import useMinerPageContext from "../../context/useMinerPageContext.js";
import { useSiteCommands } from "../../queries/useSiteCommands.ts";
import useSiteAndGroupTreeContext from "../../context/useSiteAndGroupTreeContext.js";
import { useHistory } from "react-router-dom";

import StyledSwitch from "../../../components/StyledComponents/Switch/Switch.js";

const FluxVisionTuningFormHeader = (props) => {
  const {
    setOpen,
    setWhichMinerConfigurationForm,
    heading,
  } = props;
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1rem 0"}
    >
      <H6HeadlineSmall> {heading}</H6HeadlineSmall>
      <StyledIconButton
        colors={colors}
        onClick={() => {
          setOpen(false);
          setWhichMinerConfigurationForm("");
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const FlashLEDForm = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        flashLED: "off",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setJobId,
    setStepperOpen,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = (data) => {
    console.log("data", data);
    if (data.flashLED === "on") {
      SendCommands(
        {
          commands: {
            command: "led",
            code: 102,
            led1: 4,
            led2: 4,
            msg: "Green flashing on both LEDs",
          },
          serials: selectedSerials,
          siteName: siteSelectedName,
          filter: {
            ...groupFilters,
            ...(groupSelectedName !== ""
              ? { dgname: groupSelectedName }
              : {}),
            ...(siteSelectedName !== ""
              ? { siteName: siteSelectedName }
              : {}),
          },
          fvTuning: fluxVisionTuning,
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              console.log(
                "LED flashing started for selected miners"
              );
              setMinerConfigurationModalOpen(false);
              await refetch();
              setTableKey((prev) => prev + 1);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.error(
                "Error starting LED flashing:",
                response
              );
              setToastMessage(
                "Error starting LED flashing"
              );
              setToastOpen(true);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
            }
          },
          onError: (error) => {
            console.error(
              "Error starting LED flashing:",
              error
            );
            setToastMessage("Error starting LED flashing");
            setToastOpen(true);
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          },
        }
      );
    } else {
      SendCommands(
        {
          commands: {
            command: "led",
            code: 102,
            led1: 0,
            led2: 0,
            msg: "Green flashing off",
          },
          serials: selectedSerials,
          siteName: siteSelectedName,
          filter: {
            ...groupFilters,
            ...(groupSelectedName !== ""
              ? { dgname: groupSelectedName }
              : {}),
            ...(siteSelectedName !== ""
              ? { siteName: siteSelectedName }
              : {}),
          },
          fvTuning: fluxVisionTuning,
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              console.log("LED flashing stopped");
              setMinerConfigurationModalOpen(false);
              await refetch();
              setTableKey((prev) => prev + 1);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.error(
                "Error stopping LED flashing:",
                response
              );
              setToastMessage(
                "Error stopping LED flashing"
              );
              setToastOpen(true);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
            }
          },
          onError: (error) => {
            console.error(
              "Error stopping LED flashing:",
              error
            );
            setToastMessage("Error stopping LED flashing");
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          },
        }
      );
    }
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const flashLED = watch("flashLED");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Set Flash LED"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <H8TitleMedium>Enable Flash LED</H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors} component="fieldset">
              <Controller
                name="flashLED"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch
                colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
                sx={{ color: colors.text.primary }}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {/* <span className="material-symbols-outlined">
                report
              </span> */}
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local system
                  configuration.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overriden by
                  local system configuration.
                </BodyMedium>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors.primary.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const OptimizeFanSpeedForm = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        fanInStandby: "off",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setStepperOpen,
    setJobId,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = async (data) => {
    const command = {
      command: "fvmode",
      fansInStandby: data.fanInStandby,
    };
    SendCommands(
      {
        commands: command,
        serials: selectedSerials,
        siteName: siteSelectedName,
        filter: {
          ...groupFilters,
          ...(groupSelectedName !== ""
            ? { dgname: groupSelectedName }
            : {}),
          ...(siteSelectedName !== ""
            ? { siteName: siteSelectedName }
            : {}),
        },
        fvTuning: fluxVisionTuning,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            if (data.fanInStandby === "on") {
              console.log(
                "Optimized fan mode in standby mode has been triggered with option ON. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              if (response?.jobid) {
                setStepperOpen(true);
                setJobId(response?.jobid);
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
              }
            } else {
              console.log(
                "Optimized fan mode in standby mode has been triggered with option OFF. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            }
          } else {
            console.error(
              "Error configuring fan in standby mode:",
              response.msg
            );
            setToastMessage(
              "Error configuring fan in standby mode: " +
                response.msg
            );
            setToastOpen(true);
          }
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
        onError: (error) => {
          setToastOpen(true);
          console.error(
            "Error configuring fan in standby mode:",
            error
          );
          setToastMessage(
            "Error configuring fan in standby mode"
          );
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
      }
    );
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const fanInStandby = watch("fanInStandby");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Optimize Fan Speed"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>
              Set fans to run at 20% of maximum speed when
              the miner is in Standby mode.
            </H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors}  component="fieldset">
              <Controller
                name="fanInStandby"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
                sx={{ color: colors.text.primary }}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {/* <span className="material-symbols-outlined">
                report
              </span> */}
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local system
                  configuration.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overridden by
                  local system configuration.
                </BodyMedium>
              )}
            </Box>
          </Box>
          <BodyMedium>
            <b>Note:</b> This operation requires miner
            firmware v1.0.1,v1.1.4-beta, or later.
          </BodyMedium>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors.primary.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const EnhancedEcoForm = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        optimizeEco: "off",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setStepperOpen,
    setJobId,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = async (data) => {
    const command = {
      command: "fvmode",
      optimizeEco: data.optimizeEco,
    };
    SendCommands(
      {
        commands: command,
        serials: selectedSerials,
        siteName: siteSelectedName,
        filter: {
          ...groupFilters,
          ...(groupSelectedName !== ""
            ? { dgname: groupSelectedName }
            : {}),
          ...(siteSelectedName !== ""
            ? { siteName: siteSelectedName }
            : {}),
        },
        fvTuning: fluxVisionTuning,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            if (data.optimizeEco === "on") {
              console.log(
                "Enhanced eco mode has been triggered with option ON"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.log(
                "Enhanced eco mode has been triggered with option OFF. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab or check the jobs column for each miner in the miner inventory tab"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                setStepperOpen(true);
                setJobId(response?.jobid);
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
              }
            }
          } else {
            console.error(
              "Error configuring optimized eco mode:",
              response.msg
            );
            setToastMessage(
              "Error configuring optimized eco mode: " +
                response.msg
            );
            setToastOpen(true);
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          }
        },
        onError: (error) => {
          setToastOpen(true);
          console.error(
            "Error configuring optimized eco mode:",
            error
          );
          setToastMessage(
            "Error configuring optimized eco mode"
          );
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
      }
    );
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const optimizeEco = watch("optimizeEco");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Enhanced eco mode"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>
              Search for the lowest possible J/THs when
              miner(s) operates in eco mode.
            </H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors}  component="fieldset">
              <Controller
                name="optimizeEco"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch
                colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {/* <span className="material-symbols-outlined">
                report
              </span> */}
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local system
                  configuration.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overridden by
                  local system configuration.
                </BodyMedium>
              )}
            </Box>
          </Box>
          <BodyMedium>
            <b>Note:</b> This operation requires miner
            firmware v2.0.0 or later.
          </BodyMedium>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors.primary.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const MineWOInternet = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        miningIfNetDown: "off",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setStepperOpen,
    setJobId,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = async (data) => {
    const command = {
      command: "fvmode",
      miningIfNetDown: data.miningIfNetDown,
    };
    SendCommands(
      {
        commands: command,
        serials: selectedSerials,
        siteName: siteSelectedName,
        filter: {
          ...groupFilters,
          ...(groupSelectedName !== ""
            ? { dgname: groupSelectedName }
            : {}),
          ...(siteSelectedName !== ""
            ? { siteName: siteSelectedName }
            : {}),
        },
        fvTuning: fluxVisionTuning,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            if (data.miningIfNetDown === "on") {
              console.log(
                "Mining without internet has been triggered with option ON."
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.log(
                "Mining without internet has been triggered with option OFF"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                setStepperOpen(true);
                setJobId(response?.jobid);
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
              }
            }
          } else {
            console.error(
              "Error configuring mining without internet:",
              response.msg
            );
            setToastMessage(
              "Error configuring mining without internet: " +
                response.msg
            );
            setToastOpen(true);
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          }
        },
        onError: (error) => {
          setToastOpen(true);
          console.error(
            "Error configuring mining without internet:",
            error
          );
          setToastMessage("Error: No miners connected");
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
      }
    );
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const miningIfNetDown = watch("miningIfNetDown");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Mining with no internet"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>
              Allow the miner(s) to continue mining when
              there is no internet.
            </H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors}  component="fieldset">
              <Controller
                name="miningIfNetDown"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch
                colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {/* <span className="material-symbols-outlined">
                report
              </span> */}
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local system
                  configuration.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overridden by
                  local system configuration.
                </BodyMedium>
              )}
            </Box>
          </Box>
          <BodyMedium>
            <b>Note:</b> This operation requires miner
            firmware v2.0.0 or later.
          </BodyMedium>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors?.primary?.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const StandbyMode = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        standbyMode: "on",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setStepperOpen,
    setJobId,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = async (data) => {
    const command = {
      command: "fvmode",
      sleep: data.standbyMode,
    };
    SendCommands(
      {
        commands: command,
        serials: selectedSerials,
        siteName: siteSelectedName,
        filter: {
          ...groupFilters,
          ...(groupSelectedName !== ""
            ? { dgname: groupSelectedName }
            : {}),
          ...(siteSelectedName !== ""
            ? { siteName: siteSelectedName }
            : {}),
        },
        fvTuning: fluxVisionTuning,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            if (data.standbyMode === "on") {
              console.log(
                "Standby mode has been triggered with option ON"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.log(
                "Standby mode has been triggered with option OFF"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            }
          } else {
            console.error(
              "Error setting Standby mode:",
              response.msg
            );
            setToastMessage(
              "Error setting Standby mode: " + response.msg
            );
            setToastOpen(true);
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          }
        },
        onError: (error) => {
          setToastOpen(true);
          console.error(
            "Error setting Standby mode:",
            error
          );
          setToastMessage("Error: No miners connected");
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
      }
    );
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const standbyMode = watch("standbyMode");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Standby Mode"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>
              Put miner(s) in Standby mode to reduce energy
              consumption while the control board remains
              active.
            </H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors}  component="fieldset">
              <Controller
                name="standbyMode"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch
                colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {/* <span className="material-symbols-outlined">
                report
              </span> */}
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local tuning
                  settings.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overridden by
                  local tuning settings.
                </BodyMedium>
              )}
            </Box>
          </Box>
          <BodyMedium>
            <b>Note:</b> This operation requires miner
            firmware 2024-06.06 or later.
          </BodyMedium>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors.primary.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const PersistStandbyMode = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        PersistStandbyMode: "off",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setStepperOpen,
    setJobId,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = async (data) => {
    const command = {
      command: "fvmode",
      persistStandby: data.PersistStandbyMode,
    };
    SendCommands(
      {
        commands: command,
        serials: selectedSerials,
        siteName: siteSelectedName,
        filter: {
          ...groupFilters,
          ...(groupSelectedName !== ""
            ? { dgname: groupSelectedName }
            : {}),
          ...(siteSelectedName !== ""
            ? { siteName: siteSelectedName }
            : {}),
        },
        fvTuning: fluxVisionTuning,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            if (data.PersistStandbyMode === "on") {
              console.log(
                "Persist standby mode has been triggered with option ON. "
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.log(
                "Persist standby mode has been triggered with option OFF"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            }
          } else {
            console.error(
              "Error configuring Persist standby mode:",
              response.msg
            );
            setToastMessage(
              "Error configuring Persist standby mode: " +
                response.msg
            );
            setToastOpen(true);
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          }
        },
        onError: (error) => {
          setToastOpen(true);
          console.error(
            "Error configuring Persist standby mode:",
            error
          );
          setToastMessage("Error: No miners connected");
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
      }
    );
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const PersistStandbyMode = watch("PersistStandbyMode");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Persist Standby Mode"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>
              Persist Standby mode after reboot if the miner
              is in Standby before the reboot.
            </H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors}  component="fieldset">
              <Controller
                name="PersistStandbyMode"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch
                colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local system
                  configuration.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overridden by
                  local system configuration.
                </BodyMedium>
              )}
            </Box>
          </Box>
          <BodyMedium>
            <b>Note:</b> This operation requires miner
            firmware 2.0.0 or later.
          </BodyMedium>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors.primary.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const LeakDetectMode = ({
  setMinerConfigurationModalOpen,
  setWhichMinerConfigurationForm,
  refetch,
  setTableKey,
}) => {
  const { colors } = useTheme();
  const { control, handleSubmit, watch, setValue, reset, formState: { errors, isValid } } =
    useForm({
      defaultValues: {
        LeakDetectMode: "off",
      },
    });

  const {
    mutate: SendCommands,
    isPending,
    isSuccess,
  } = useSiteCommands();
  const {
    selectedSerials,
    groupFilters,
    setStepperOpen,
    setJobId,
    fluxVisionTuning,
    setFluxVisionTuning,
  } = useMinerPageContext();
  const { siteSelectedName, groupSelectedName } =
    useSiteAndGroupTreeContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const navigate = useHistory();

  const onSubmit = async (data) => {
    const command = {
      command: "fvmode",
      leakDetected: data.LeakDetectMode,
    };
    SendCommands(
      {
        commands: command,
        serials: selectedSerials,
        siteName: siteSelectedName,
        filter: {
          ...groupFilters,
          ...(groupSelectedName !== ""
            ? { dgname: groupSelectedName }
            : {}),
          ...(siteSelectedName !== ""
            ? { siteName: siteSelectedName }
            : {}),
        },
        fvTuning: fluxVisionTuning,
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            if (data.LeakDetectMode === "on") {
              console.log(
                "Leak detect mode has been triggered with option ON. "
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            } else {
              console.log(
                "Leak detect mode has been triggered with option OFF"
              );
              await refetch();
              setTableKey((prev) => prev + 1);
              setMinerConfigurationModalOpen(false);
              setWhichMinerConfigurationForm(null);
              if (response?.jobid) {
                // navigate.push(
                //   `/jobDetails?jobID=${response?.jobid}`
                // );
                setStepperOpen(true);
                setJobId(response?.jobid);
              }
            }
          } else {
            console.error(
              "Error configuring leak detect mode:",
              response.msg
            );
            setToastMessage(
              "Error configuring leak detect mode: " +
                response.msg
            );
            setToastOpen(true);
            setMinerConfigurationModalOpen(false);
            setWhichMinerConfigurationForm(null);
          }
        },
        onError: (error) => {
          setToastOpen(true);
          console.error(
            "Error configuring Leak detect mode:",
            error
          );
          setToastMessage("Error: No miners connected");
          setMinerConfigurationModalOpen(false);
          setWhichMinerConfigurationForm(null);
        },
      }
    );
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  const LeakDetectMode = watch("LeakDetectMode");

  return (
    <>
      <FluxVisionTuningFormHeader
        setOpen={setMinerConfigurationModalOpen}
        setWhichMinerConfigurationForm={
          setWhichMinerConfigurationForm
        }
        heading="Leak Detect Mode"
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"1.5rem"}
          paddingBottom={"1.5rem"}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <H8TitleMedium>
              Put the miner in standby mode when a leak is
              detected.
            </H8TitleMedium>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors}  component="fieldset">
              <Controller
                name="LeakDetectMode"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="on"
                        control={<StyledRadio colors={colors} />}
                        label="On"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="off"
                        control={<StyledRadio colors={colors} />}
                        label="Off"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            paddingRight={"0.5rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"space-between"}
              flexGrow={1}
              alignItems={"center"}
            >
              <H8TitleMedium>
                {" "}
                FluxVision Tuning
              </H8TitleMedium>
              <StyledSwitch
                colors={colors}
                checked={fluxVisionTuning}
                onChange={onFluxVisionTuningChange}
                defaultChecked={true}
              />
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={2}
            >
              {/* <span className="material-symbols-outlined">
                report
              </span> */}
              {fluxVisionTuning ? (
                <BodyMedium>
                  FluxVision Tuning mode is enabled, meaning
                  this command will override local system
                  configuration.
                </BodyMedium>
              ) : (
                <BodyMedium>
                  FluxVision Tuning mode is disabled,
                  meaning this command may be overridden by
                  local system configuration.
                </BodyMedium>
              )}
            </Box>
          </Box>
          <BodyMedium>
            <b>Note:</b> This operation requires HYDRO miner
            firmware 2.1.0-beta or later.
          </BodyMedium>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton colors={colors}
          disabled={!isValid || isPending}
          onClick={handleSubmit(onSubmit)}
          endIcon={
            isPending && (
              <CircularProgress
                size={24}
                sx={{ color: colors.primary.main }}
              />
            )
          }
        >
          {isPending
            ? "Submitting..."
            : isSuccess
            ? "Submitted"
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const ConfigurationModal = (props) => {
  const { refetch, setTableKey } = props;
  const { colors } = useTheme();
  const {
    MinerConfigurationModalOpen,
    setMinerConfigurationModalOpen,
    whichMinerConfigurationForm,
    setWhichMinerConfigurationForm,
  } = useMinerPageContext();

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"lg"}
      fullWidth
      open={MinerConfigurationModalOpen}
      hideBackdrop
    >
      {whichMinerConfigurationForm === "flashLED" && (
        <FlashLEDForm
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}

      {whichMinerConfigurationForm === "fanInStandby" && (
        <OptimizeFanSpeedForm
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}

      {whichMinerConfigurationForm === "optimizeEco" && (
        <EnhancedEcoForm
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}
      {whichMinerConfigurationForm ===
        "mineWithoutInternet" && (
        <MineWOInternet
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}
      {whichMinerConfigurationForm === "standbyMode" && (
        <StandbyMode
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}
      {whichMinerConfigurationForm ===
        "persistStandbyMode" && (
        <PersistStandbyMode
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}
      {whichMinerConfigurationForm === "leakDetected" && (
        <LeakDetectMode
          setMinerConfigurationModalOpen={
            setMinerConfigurationModalOpen
          }
          setWhichMinerConfigurationForm={
            setWhichMinerConfigurationForm
          }
          refetch={refetch}
          setTableKey={setTableKey}
        />
      )}
      {}
    </StyledDialog>
  );
};

const MinerConfigurationDialog = (props) => {
  const { refetch, setTableKey } = props;
  return (
    <>
      <ConfigurationModal
        refetch={refetch}
        setTableKey={setTableKey}
      />
    </>
  );
};

export default MinerConfigurationDialog;
